import React from 'react'

export type HospitalityModule = 'accommodation' | 'meals'

interface ModuleContextValue {
  module: HospitalityModule
  setModule: (module: HospitalityModule) => void
}

const MODULE_STORAGE_KEY = 'eunice.hospitality.module'

const ModuleContext = React.createContext<ModuleContextValue | null>(null)

function getStoredModule(): HospitalityModule {
  if (typeof window === 'undefined') {
    return 'accommodation'
  }

  const storedModule = window.localStorage.getItem(MODULE_STORAGE_KEY)
  return storedModule === 'meals' ? 'meals' : 'accommodation'
}

export function ModuleProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [module, setModuleState] = React.useState<HospitalityModule>(() => getStoredModule())

  const setModule = React.useCallback((nextModule: HospitalityModule) => {
    setModuleState(nextModule)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MODULE_STORAGE_KEY, nextModule)
    }
  }, [])

  const value = React.useMemo(() => ({
    module,
    setModule,
  }), [module, setModule])

  return <ModuleContext value={value}>{children}</ModuleContext>
}

export function useHospitalityModule() {
  const context = React.use(ModuleContext)

  if (!context) {
    throw new Error('useHospitalityModule must be used within a ModuleProvider')
  }

  return context
}
